import { useState } from "react";
import { Button, ConfirmDialog, NumberInput, useToast } from "@a2z/ui";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type {
  OnlinePaymentProvider,
  OnlinePaymentStatus,
} from "@/lib/api/types";
import { formatCurrency } from "@/lib/formatting/currency";
import { useStoreOrderPaymentTransactionQuery } from "@/modules/stores/services/queries/useStoreOrderPaymentTransactionQuery";
import { useRefundStoreOrderMutation } from "@/modules/stores/services/mutations/useRefundStoreOrderMutation";

// Gateway brand names are proper nouns (same in en/ar), so they're not i18n keys.
const PROVIDER_LABEL: Record<OnlinePaymentProvider, string> = {
  paymob: "Paymob",
  paytabs: "PayTabs",
  kashier: "Kashier",
  fawry: "Fawry",
  stripe: "Stripe",
};

const STATUS_KEY: Record<OnlinePaymentStatus, string> = {
  initiated: keys.orders.onlinePayStatusInitiated,
  pending: keys.orders.onlinePayStatusPending,
  paid: keys.orders.onlinePayStatusPaid,
  failed: keys.orders.onlinePayStatusFailed,
  refunded: keys.orders.onlinePayStatusRefunded,
  cancelled: keys.orders.onlinePayStatusCancelled,
  expired: keys.orders.onlinePayStatusExpired,
};

const STATUS_TONE: Record<OnlinePaymentStatus, string> = {
  paid: "var(--success-500)",
  refunded: "var(--text-secondary)",
  failed: "var(--error-500)",
  cancelled: "var(--text-muted)",
  expired: "var(--text-muted)",
  pending: "var(--warning-500)",
  initiated: "var(--warning-500)",
};

type OnlinePaymentPanelProps = {
  storeId: string;
  orderId: string;
  locale: string;
};

// Admin order-detail card: how an online order was paid + a refund action
// (full or partial). Renders nothing for COD/manual orders.
export function OnlinePaymentPanel({
  storeId,
  orderId,
  locale,
}: OnlinePaymentPanelProps) {
  const t = useTranslations();
  const toast = useToast();
  const { transaction } = useStoreOrderPaymentTransactionQuery({
    storeId,
    orderId,
  });
  const refund = useRefundStoreOrderMutation();
  const [amount, setAmount] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (!transaction) return null;

  const tone = STATUS_TONE[transaction.status];
  const canRefund = transaction.status === "paid";
  const maxAmount = transaction.amount;
  const providerLabel = PROVIDER_LABEL[transaction.provider];
  const fullLabel = formatCurrency(maxAmount, locale, transaction.currency);

  const trimmed = amount.trim();
  const parsedAmount = trimmed === "" ? maxAmount : Number(amount);
  // Empty input = full refund; otherwise 0 < amount ≤ paid.
  const isAmountValid =
    trimmed === "" ||
    (Number.isFinite(parsedAmount) &&
      parsedAmount > 0 &&
      parsedAmount <= maxAmount);

  const submitRefund = async () => {
    if (!isAmountValid) return;
    try {
      await refund.mutateAsync({
        storeId,
        orderId,
        ...(trimmed === "" ? {} : { amount: parsedAmount }),
      });
      toast.success(t(keys.orders.refundSuccess));
      setConfirmOpen(false);
      setAmount("");
    } catch {
      toast.error(t(keys.orders.refundError));
    }
  };

  return (
    <div className="rounded-2xl border border-[color:var(--border-light)] bg-[color:var(--surface)] px-6 py-5 shadow-[var(--shadow-sm)]">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-[color:var(--text-primary)]">
          {t(keys.orders.onlinePaymentTitle)}
        </div>
        <span
          className="inline-flex items-center gap-1.5 rounded-lg border border-[color:var(--border-light)] px-2.5 py-1 text-xs font-semibold"
          style={{ color: tone }}
        >
          <span
            className="h-1.5 w-1.5 rounded-full"
            style={{ backgroundColor: tone }}
            aria-hidden="true"
          />
          {t(STATUS_KEY[transaction.status])}
        </span>
      </div>

      <div className="mt-3 text-sm text-[color:var(--text-muted)]">
        {t(keys.orders.onlinePayProvider)}:{" "}
        <span className="font-semibold text-[color:var(--text-primary)]">
          {providerLabel}
        </span>
      </div>
      <div className="mt-2 text-sm text-[color:var(--text-muted)]">
        {t(keys.orders.onlinePayAmount)}:{" "}
        <span className="font-semibold text-[color:var(--text-primary)]">
          {fullLabel}
        </span>
      </div>
      {transaction.providerTxnId ? (
        <div className="mt-2 text-sm text-[color:var(--text-muted)]">
          {t(keys.orders.onlinePayReference)}:{" "}
          <span className="font-mono text-[color:var(--text-primary)]" dir="ltr">
            {transaction.providerTxnId}
          </span>
        </div>
      ) : null}

      {canRefund ? (
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-xs text-[color:var(--text-muted)]">
            {t(keys.orders.refundAmountLabel)}
            <NumberInput
              name="refundAmount"
              min={0}
              allowDecimal
              placeholder={fullLabel}
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
            />
          </label>
          <Button
            type="button"
            variant="primary"
            className="h-9 rounded-lg px-3 text-xs font-semibold"
            disabled={!isAmountValid || refund.isPending}
            onClick={() => setConfirmOpen(true)}
          >
            {t(keys.orders.refundAction)}
          </Button>
        </div>
      ) : null}

      <ConfirmDialog
        isOpen={confirmOpen}
        title={t(keys.orders.refundConfirmTitle)}
        body={t(keys.orders.refundConfirmBody, {
          amount: formatCurrency(parsedAmount, locale, transaction.currency),
          provider: providerLabel,
        })}
        confirmLabel={t(keys.orders.refundAction)}
        cancelLabel={t(keys.common.back)}
        isSubmitting={refund.isPending}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => void submitRefund()}
      />
    </div>
  );
}
